import CoreBase from '../../services/engines/CoreBase';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import calendarCache from './CalendarCache';
import { CalEventStates } from '../Types/CalEventStates';
import { offsetToMs } from '../Types/ReminderOffset';

const _name = 'CalendarScheduler';

// Poll interval — checks every event's ems/remind against Date.now().
const TICK_MS = 15 * 1000;

/**
 * CalendarScheduler — periodically checks upcoming CalEvents for state transitions.
 * Pure timing/transition logic — no rendering, no SignalR, no cache mutation beyond stateId.
 * Uses the calendarCache singleton directly — same instance CalendarEngine reads from.
 * Notifies 'dataChanged' directly via ComponentNotifier when a transition happens —
 * same channel/target CalendarEngine uses, no callback injection between singletons.
 *
 * Transition:
 *   Active -> Visible  when now >= ems - offsetToMs(remind)
 *             (reminder fires, or ems itself passed if no remind set — either
 *             way the event becomes a FloatingCard)
 *
 * Visible is terminal from the scheduler's point of view — event.isRinging()
 * (overdue, or reminder time passed) drives "ringing" styling on the card,
 * computed live, no stored state — survives page refresh. User action
 * (dismiss/minimize via CalendarEngine) moves Visible back to Completed/Active.
 */
class CalendarScheduler extends CoreBase {
    _name = _name;
    _timer = null;

    /**
     * Starts the polling loop. Safe to call repeatedly — no-op if already running.
     */
    start = () => {
        try {
            if (this._timer) {
                this.logDebug('start', 'timer already set');
                return;
            }

            this._timer = setInterval(this._tick, TICK_MS);
            // Run once immediately so overdue/visible state is correct right after load.
            this._tick();
        } catch (error) {
            this.logError('start', error.message);
        }
    };

    /**
     * Stops the polling loop. Safe to call multiple times.
     */
    stop = () => {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    };

    // ─── private ──────────────────────────────────────────

    _tick = () => {
        try {
            const events = calendarCache.getUpcoming();
            if (events.length === 0) return;

            const now = Date.now();
            let changed = false;

            for (const event of events) {
                if (this._applyTransition(event, now)) changed = true;
            }

            if (changed) this._notify();
        } catch (error) {
            this.logError('_tick', error.message);
        }
    };

    /**
     * Active -> Visible when the reminder (or the event itself, if no remind
     * is set) is due. Puts the event on screen as a FloatingCard either way.
     * @param {CalEventObj} event
     * @param {number} now - Date.now() snapshot for this tick
     * @returns {boolean} true if a transition was applied
     */
    _applyTransition = (event, now) => {
        if (!event.ems) return false;
        if (event.stateId !== CalEventStates.Active) return false;

        const offsetMs = offsetToMs(event.remind);
        const remindAt = offsetMs > 0 ? event.ems - offsetMs : event.ems;

        if (now < remindAt) return false;

        this._setState(event, CalEventStates.Visible);
        return true;
    };

    /**
     * Updates stateId on both the cache and the in-memory event instance,
     * so callers reading the array just returned by getUpcoming() see the new value.
     * @param {CalEventObj} event
     * @param {number} stateId - see CalEventStates
     */
    _setState = (event, stateId) => {
        calendarCache.setState(event.rk, stateId);
        event.stateId = stateId;
    };

    /**
     * Notifies the UI of a transition — same 'dataChanged'/'CalendarEngine'
     * channel CalendarEngine._notify uses, so useCalendar's existing
     * subscription picks it up without changes.
     */
    _notify = () => {
        ComponentNotifier.notify('dataChanged', 'CalendarEngine', {
            upcoming: calendarCache.getUpcoming(),
            visible: calendarCache.getVisible(),
        });
    };
}

const calendarScheduler = new CalendarScheduler();
export default calendarScheduler;
